import type {Accommodation} from "#src/types/accommodation.types";
import type {ApprovalMatrix} from "#src/types/approval-matrix.types";
import type {AttachableType, Attachment} from "#src/types/attachments.types";
import type {BudgetAllocation} from "#src/types/budget-allocation.types";
import type {BudgetCode} from "#src/types/budget-code.types";
import type {Classification} from "#src/types/classification.types";
import type {CompanyCategory} from "#src/types/company-category.types";
import type {CompanySubcategory, CompanySubcategoryWithCategory} from "#src/types/company-subcategory.types";
import type {CompanyType, CompanyTypeWithRelations} from "#src/types/company-type.types";
import type {Company, CompanyWithRelations} from "#src/types/company.types";
import type { ComponentClass } from "#src/types/component-class.types";
import type {
	ComponentCounterEligibleRaw,
	ComponentCounterHistory,
} from "#src/types/component-counter.types";
import type { ComponentSchedule } from "#src/types/component-schedule.types";
import type { ComponentType } from "#src/types/component-type.types";
import type { Component } from "#src/types/component.types";
import type {Country} from "#src/types/country.types";
import type {Appraisal, AppraisalEligibleRaw} from "#src/types/crew-appraisal.types";
import type { Crew } from "#src/types/crew.types";
import type {ContractAllowance, CrewContract} from "#src/types/crewContract.types";
import type {CrewMovement} from "#src/types/crewMovement.types";
import type {CrewRequest} from "#src/types/crewRequest.types";
import type {Currency} from "#src/types/currency.types";
import type { DefectType } from "#src/types/defect-type.types";
import type { Defect } from "#src/types/defect.types";
import type {Department} from "#src/types/department.types";
import type { IMPA, IMPAWithRelations } from "#src/types/impa.types";
import type {Indent} from "#src/types/indent.types";
import type { Inventory } from "#src/types/inventory.types";
import type { JobClass } from "#src/types/job-class.types";
import type { Job } from "#src/types/job.types";
import type { Location } from "#src/types/location.types";
import type { LogbookProperty } from "#src/types/logbook-property.types";
import type { MaintenanceMethod } from "#src/types/maintenance-method.types";
import type {NavigationCommunication} from "#src/types/navigation-communication.types";
import type {PaymentDestination} from "#src/types/paymentDestination.types";
import type {PaymentFactor} from "#src/types/paymentFactor.types";
import type { Permit } from "#src/types/permit.types";
import type { Port } from "#src/types/port.types";
import type { Priority } from "#src/types/priority.types";
import type { PropertyRangeType } from "#src/types/property-range-type.types";
import type { Rank } from "#src/types/rank.types";
import type { ReasonLabel } from "#src/types/reason-label.types";
import type { Rfq } from "#src/types/rfq.types";
import type {ServiceGroup} from "#src/types/service-group.types";
import type { StockClass } from "#src/types/stock-class.types";
import type { StockType } from "#src/types/stock-type.types";
import type { Stock } from "#src/types/stock.types";
import type { Store } from "#src/types/store.types";
import type { Transaction, TransactionDetail } from "#src/types/transaction.types";
import type { UOM } from "#src/types/uom.types";
import type { VendorQuotation } from "#src/types/vendor-quotation.types";
import type {Vessel, VesselWithRelations} from "#src/types/vessel.types";
import type {BcItem} from "#src/types/vessel/bc-item.types";
import type {BcType} from "#src/types/vessel/bc-type.types";
import type {Capacity} from "#src/types/vessel/capacity.types";
import type {CargoCrane} from "#src/types/vessel/cargo-crane.types";
import type {CargoHold} from "#src/types/vessel/cargo-hold.types";
import type {Certificate} from "#src/types/vessel/certificate.types";
import type {ContainerCraneDetail} from "#src/types/vessel/container-crane-detail.types";
import type {ContainerDeckStrength} from "#src/types/vessel/container-deck-strength.types";
import type {ContainerHold} from "#src/types/vessel/container-hold-bulk.types";
import type {ContainerHoldSpec} from "#src/types/vessel/container-hold-spec.types";
import type {ContainerLashing} from "#src/types/vessel/container-lashing.types";
import type {ContainerMainSpec} from "#src/types/vessel/container-main-spec.types";
import type {ContainerReeferSlot} from "#src/types/vessel/container-reefer-slot.types";
import type {Contract} from "#src/types/vessel/contract.types";
import type {DeckEquipment} from "#src/types/vessel/deck-equipment.types";
import type {DeckMachinery} from "#src/types/vessel/deck-machinery.types";
import type {Dimension} from "#src/types/vessel/dimension.types";
import type {Dwt} from "#src/types/vessel/dwt.types";
import type {HatchCover} from "#src/types/vessel/hatch-cover.types";
import type {InsuranceInstallment} from "#src/types/vessel/insurance-installment.types";
import type {Insurance} from "#src/types/vessel/insurance.types";
import type {Lopm} from "#src/types/vessel/lopm.types";
import type {MainMachinery} from "#src/types/vessel/main-machinery.types";
import type {OffshoreAzimuthThruster} from "#src/types/vessel/offshore-azimuth-thruster.types";
import type {OffshoreBasicSpec} from "#src/types/vessel/offshore-basic-spec.types";
import type {OffshoreCrane} from "#src/types/vessel/offshore-crane.types";
import type {OffshoreFirefighting} from "#src/types/vessel/offshore-firefighting.types";
import type {OffshoreTunnelThruster} from "#src/types/vessel/offshore-tunnel-thruster.types";
import type {OffshoreWinch} from "#src/types/vessel/offshore-winch.types";
import type {SlopDetails} from "#src/types/vessel/slop-details.types";
import type {SpeedCondition} from "#src/types/vessel/speed-condition.types";
import type {SpeedConsumptionData} from "#src/types/vessel/speed-consumption-data.types";
import type {TankType} from "#src/types/vessel/tank-type.types";
import type {Tank} from "#src/types/vessel/tank.types";
import type {TankerBallastEducator} from "#src/types/vessel/tanker-ballast-educator.types";
import type {TankerBallastPump} from "#src/types/vessel/tanker-ballast-pump.types";
import type {TankerCargoEducator} from "#src/types/vessel/tanker-cargo-educator.types";
import type {TankerCargoHeating} from "#src/types/vessel/tanker-cargo-heating.types";
import type {TankerCargoPump} from "#src/types/vessel/tanker-cargo-pump.types";
import type {TankerCraneDetail} from "#src/types/vessel/tanker-crane-detail.types";
import type {TankerInertGas} from "#src/types/vessel/tanker-inert-gas.types";
import type {TankerManifold} from "#src/types/vessel/tanker-manifold.types";
import type {TankerPvVapor} from "#src/types/vessel/tanker-pv-vapor.types";
import type {TankerVaporReturn} from "#src/types/vessel/tanker-vapor-return.types";
import type { WorkOrderClass } from "#src/types/work-order-class.types";
import type {
	WorkOrder,
	WorkOrderManPower,
	WorkOrderSparePart,
} from "#src/types/work-order.types";

import {createAppEntityCrudConfig} from "#src/hooks/useAppEntityCrud/createAppEntityCrudConfig";

export const PORTS_CRUD_CONFIG = createAppEntityCrudConfig<Port, Port>({
	apiSegment: "ports",
	viewEntityName: "Port",
});

export const PERMITS_CRUD_CONFIG = createAppEntityCrudConfig<Permit, Permit>({
	apiSegment: "permits",
	viewEntityName: "Permit",
});

export const VESSELS_CRUD_CONFIG = createAppEntityCrudConfig<Vessel, VesselWithRelations>({
	apiSegment: "vessels",
	viewEntityName: "Vessel",
});

export const COMPANY_CATEGORIES_CRUD_CONFIG = createAppEntityCrudConfig<CompanyCategory, CompanyCategory>({
	apiSegment: "company-categories",
	// getAllUrl: "company-categories",
	viewEntityName: "CompanyCategory",
});

export const COMPANY_SUBCATEGORIES_CRUD_CONFIG = createAppEntityCrudConfig<CompanySubcategory, CompanySubcategoryWithCategory>({
	apiSegment: "company-subcategories",
	viewEntityName: "CompanySubcategory",
});

export const COMPANY_TYPES_CRUD_CONFIG = createAppEntityCrudConfig<CompanyType, CompanyTypeWithRelations>({
	apiSegment: "company-types",
	viewEntityName: "CompanyType",
});

export const CLASSIFICATIONS_CRUD_CONFIG = createAppEntityCrudConfig<Classification, Classification>({
	apiSegment: "classifications",
	viewEntityName: "Classification",
});

export const COMPANY_CRUD_CONFIG = createAppEntityCrudConfig<Company, CompanyWithRelations>({
	apiSegment: "companies",
	viewEntityName: "Company",
	getByIdUrlTemplate: (id: string) => `companies/${id}`,
});

export const UOMS_CRUD_CONFIG = createAppEntityCrudConfig<UOM, UOM>({
  apiSegment: "UOMs",
  viewEntityName: "UOM",
});

export const IMPAS_CRUD_CONFIG = createAppEntityCrudConfig<IMPA, IMPAWithRelations>({
	apiSegment: "impas",
	viewEntityName: "IMPA",
});

export const JOB_CLASSES_CRUD_CONFIG = createAppEntityCrudConfig<JobClass, JobClass>({
  apiSegment: "job-classes",
  viewEntityName: "JobClass",
});

export const JOBS_CRUD_CONFIG = createAppEntityCrudConfig<Job, Job>({
  apiSegment: "jobs",
  viewEntityName: "Job",
});

export const MAINTENANCE_METHODS_CRUD_CONFIG = createAppEntityCrudConfig<MaintenanceMethod, MaintenanceMethod>({
  apiSegment: "maintenance-methods",
  viewEntityName: "MaintenanceMethod",
});

export const DEFECT_TYPES_CRUD_CONFIG = createAppEntityCrudConfig<DefectType, DefectType>({
  apiSegment: "defect-types",
  viewEntityName: "DefectType",
});

export const DEFECTS_CRUD_CONFIG = createAppEntityCrudConfig<Defect, Defect>({
  apiSegment: "defects",
  viewEntityName: "Defect",
  getByIdUrlTemplate: id => `defects/${id}?include_details=true`,
});

export const WORK_ORDER_CLASSES_CRUD_CONFIG = createAppEntityCrudConfig<WorkOrderClass, WorkOrderClass>({
  apiSegment: "work-order-classes",
  viewEntityName: "WorkOrderClass",
});

export const WORK_ORDERS_CRUD_CONFIG = createAppEntityCrudConfig<WorkOrder, WorkOrder>({
  apiSegment: "work-orders",
  viewEntityName: "WorkOrder",
});

export const WORK_ORDER_MANPOWER_CRUD_CONFIG = createAppEntityCrudConfig<WorkOrderManPower, WorkOrderManPower>({
  apiSegment: "work-order-manpower",
  viewEntityName: "WorkOrderManPower",
});

export const WORK_ORDER_SPARE_PARTS_CRUD_CONFIG = createAppEntityCrudConfig<WorkOrderSparePart, WorkOrderSparePart>({
  apiSegment: "work-order-spare-parts",
  viewEntityName: "WorkOrderSparePart",
});

export const COMPONENT_CLASSES_CRUD_CONFIG = createAppEntityCrudConfig<ComponentClass, ComponentClass>({
  apiSegment: "component-classes",
  viewEntityName: "ComponentClass",
  deleteUrlTemplate: id => `component-classes/${id}?hard=true`,
});

export const COMPONENT_TYPES_CRUD_CONFIG = createAppEntityCrudConfig<ComponentType, ComponentType>({
  apiSegment: "component-types",
  viewEntityName: "ComponentType",
	deleteUrlTemplate: id => `component-types/${id}?hard=true`,

});

export const STOCK_CLASSES_CRUD_CONFIG = createAppEntityCrudConfig<StockClass, StockClass>({
  apiSegment: "stock-classes",
  viewEntityName: "StockClass",
});

export const STOCKS_CRUD_CONFIG = createAppEntityCrudConfig<Stock, Stock>({
  apiSegment: "stocks",
  viewEntityName: "Stock",
});

export const STOCK_TYPES_CRUD_CONFIG = createAppEntityCrudConfig<StockType, StockType>({
  apiSegment: "stock-types",
  viewEntityName: "StockType",
});

export const LOCATIONS_CRUD_CONFIG = createAppEntityCrudConfig<Location, Location>({
	apiSegment: "locations",
	viewEntityName: "Location",
});

export const STORES_CRUD_CONFIG = createAppEntityCrudConfig<Store, Store>({
	apiSegment: "stores",
	viewEntityName: "Store",
});

export const INVENTORY_CRUD_CONFIG = createAppEntityCrudConfig<Inventory, Inventory>({
	apiSegment: "inventory",
	viewEntityName: "Inventory",
});

export const TRANSACTIONS_CRUD_CONFIG = createAppEntityCrudConfig<Transaction, Transaction>({
	apiSegment: "transactions",
	viewEntityName: "Transaction",
});

export function transactionDetailsListUrl(transactionId: number | string) {
	return `transactions/${transactionId}/details`;
}

export function transactionDetailByIdUrl(transactionId: number | string, detailId: number | string) {
	return `transactions/${transactionId}/details/${detailId}`;
}

/** Nested under `transactions/{transactionId}/details`. */
export function createTransactionDetailsCrudConfig(transactionId: number | string) {
	const base = transactionDetailsListUrl(transactionId);
	return createAppEntityCrudConfig<TransactionDetail, TransactionDetail>({
		apiSegment: base,
		viewEntityName: "TransactionDetail",
		getAllUrl: base,
		createUrl: base,
		getByIdUrlTemplate: id => transactionDetailByIdUrl(transactionId, id),
		updateUrlTemplate: id => transactionDetailByIdUrl(transactionId, id),
		deleteUrlTemplate: id => transactionDetailByIdUrl(transactionId, id),

	});
}

export const RANKS_CRUD_CONFIG = createAppEntityCrudConfig<Rank, Rank>({
	apiSegment: "crew/ranks",
	viewEntityName: "Rank",
});

export const PROPERTY_RANGE_TYPES_CRUD_CONFIG = createAppEntityCrudConfig<PropertyRangeType, PropertyRangeType>({
	apiSegment: "property-range-types",
	viewEntityName: "PropertyRangeType",
});

export const LOGBOOK_PROPERTIES_CRUD_CONFIG = createAppEntityCrudConfig<LogbookProperty, LogbookProperty>({
	apiSegment: "logbook/properties",
	viewEntityName: "LogbookProperty",
});

export const CREW_CRUD_CONFIG = createAppEntityCrudConfig<Crew, Crew>({
	apiSegment: "crew",
	viewEntityName: "Crew",
});

/** Eligible crew for monthly appraisals (GET crew/appraisals/monthly/eligible/{vesselId}). */
export function createAppraisalEligibleCrudConfig(vesselId: number | string) {
	return createAppEntityCrudConfig<AppraisalEligibleRaw, AppraisalEligibleRaw>({
		apiSegment: "crew/appraisals/monthly/eligible",
		getAllUrl: `crew/appraisals/monthly/eligible/${vesselId}`,
		viewEntityName: "AppraisalEligible",
		skipDefaultIdDescListSort: true,
	});
}

/** Appraisals list / getById / update / delete (GET|PUT|DELETE crew/appraisals). */
export const APPRAISALS_CRUD_CONFIG = createAppEntityCrudConfig<Appraisal, Appraisal>({
	apiSegment: "crew/appraisals",
	viewEntityName: "Appraisal",
});

export function createPaymentDestinationsCrudConfig(crewId: number | string) {
	const base = "crew/payment-destinations";
	return createAppEntityCrudConfig<PaymentDestination, PaymentDestination>({
		apiSegment: base,
		getAllUrl: `${base}/crew/${crewId}`,
		createUrl: `${base}/crew/${crewId}`,
		viewEntityName: "Payment Destination",
		skipDefaultIdDescListSort: true,
	});
}

export function createCrewContractsCrudConfig(crewId: number | string) {
	const base = "crew/contracts";
	return createAppEntityCrudConfig<CrewContract, CrewContract>({
		apiSegment: base,
		getAllUrl: `${base}/crew/${crewId}`,
		createUrl: `${base}/crew/${crewId}`,
		viewEntityName: "Crew Contract",
		skipDefaultIdDescListSort: true,
	});
}

export const CREW_REQUESTS_CRUD_CONFIG = createAppEntityCrudConfig<CrewRequest, CrewRequest>({
	apiSegment: "crew/requests",
	viewEntityName: "Crew Request",
	skipDefaultIdDescListSort: true,
});

export function createCrewRequestsCrudConfig(crewId: number | string) {
	const base = "crew/requests";
	return createAppEntityCrudConfig<CrewRequest, CrewRequest>({
		apiSegment: base,
		getAllUrl: `${base}/crew/${crewId}`,
		createUrl: base,
		viewEntityName: "Crew Request",
		skipDefaultIdDescListSort: true,
	});
}

export const CREW_MOVEMENTS_CRUD_CONFIG = createAppEntityCrudConfig<CrewMovement, CrewMovement>({
	apiSegment: "crew/movements",
	viewEntityName: "Crew Movement",
	skipDefaultIdDescListSort: true,
});

export function createCrewMovementsByCrewConfig(crewId: number | string) {
	return createAppEntityCrudConfig<CrewMovement, CrewMovement>({
		apiSegment: "crew/movements",
		getAllUrl: `crew/movements/crew/${crewId}`,
		viewEntityName: "Crew Movement",
		skipDefaultIdDescListSort: true,
	});
}

export function contractAllowancesListUrl(contractId: number | string) {
	return `crew/contracts/${contractId}/allowances`;
}

export function contractAllowanceByIdUrl(contractId: number | string, allowanceId: number | string) {
	return `crew/contracts/${contractId}/allowances/${allowanceId}`;
}

/** Nested under `crew/contracts/{contractId}/allowances`. */
export function createContractAllowancesCrudConfig(contractId: number | string) {
	const base = contractAllowancesListUrl(contractId);
	return createAppEntityCrudConfig<ContractAllowance, ContractAllowance>({
		apiSegment: base,
		viewEntityName: "Contract Allowance",
		getAllUrl: base,
		createUrl: base,
		getByIdUrlTemplate: id => contractAllowanceByIdUrl(contractId, id),
		updateUrlTemplate: id => contractAllowanceByIdUrl(contractId, id),
		deleteUrlTemplate: id => contractAllowanceByIdUrl(contractId, id),
		skipDefaultIdDescListSort: true,
	});
}

/** Polymorphic attachments list/delete. Upload stays multipart via useAttachmentsCrud. */
export function createAttachmentsCrudConfig(
	attachableType: AttachableType,
	attachableId: number | string,
) {
	return createAppEntityCrudConfig<Attachment, Attachment>({
		apiSegment: "attachments",
		viewEntityName: "Attachment",
		getAllUrl: `attachments/list/${attachableType}/${attachableId}`,
		createUrl: "attachments/upload",
		deleteUrlTemplate: id => `attachments/${id}`,
		skipDefaultIdDescListSort: true,
	});
}

export const PAYMENT_FACTORS_CRUD_CONFIG = createAppEntityCrudConfig<PaymentFactor, PaymentFactor>({
	apiSegment: "crew/payment-factors",
	viewEntityName: "Payment Factor",
	skipDefaultIdDescListSort: true,
});

export const COMPONENTS_CRUD_CONFIG = createAppEntityCrudConfig<Component, Component>({
	apiSegment: "components",
	viewEntityName: "Component",
});

export const COMPONENT_SCHEDULES_CRUD_CONFIG = createAppEntityCrudConfig<ComponentSchedule, ComponentSchedule>({
	apiSegment: "component-schedules",
	viewEntityName: "ComponentSchedule",
});

/** Eligible counter components list (GET component-counters/eligible). */
export const COMPONENT_COUNTERS_ELIGIBLE_CRUD_CONFIG = createAppEntityCrudConfig<
	ComponentCounterEligibleRaw,
	ComponentCounterEligibleRaw
>({
	apiSegment: "component-counters/eligible",
	viewEntityName: "ComponentCounterEligible",
});

/**
 * Counter history list (GET component-counters).
 * Bulk dirty-only POST uses a custom payload shape — see ComponentCounters/api.ts.
 */
export const COMPONENT_COUNTERS_CRUD_CONFIG = createAppEntityCrudConfig<
	ComponentCounterHistory,
	ComponentCounterHistory
>({
	apiSegment: "component-counters",
	viewEntityName: "ComponentCounter",
});

export const COUNTRIES_CRUD_CONFIG = createAppEntityCrudConfig<Country, Country>({
	apiSegment: "countries",
});

export const CURRENCIES_CRUD_CONFIG = createAppEntityCrudConfig<Currency, Currency>({
	apiSegment: "currencies",
});

export const CARGO_HOLDS_CRUD_CONFIG = createAppEntityCrudConfig<CargoHold, CargoHold>({
	apiSegment: "bulk-carrier-specifications/cargo-holds",
	viewEntityName: "Cargo Hold",
});

// --- Vessel singleton specs ---

export const ACCOMMODATIONS_CRUD_CONFIG = createAppEntityCrudConfig<Accommodation, Accommodation>({
	apiSegment: "accommodations",

	getByIdUrlTemplate: id => `accommodations?vessel_id=${id}`,
});

export const NAVIGATION_COMMUNICATION_CRUD_CONFIG = createAppEntityCrudConfig<NavigationCommunication, NavigationCommunication>({
	apiSegment: "navigation-communication",

	getByIdUrlTemplate: id => `navigation-communication?vessel_id=${id}`,
});

export const DIMENSIONS_CRUD_CONFIG = createAppEntityCrudConfig<Dimension, Dimension>({
	apiSegment: "dimensions",

	getByIdUrlTemplate: id => `dimensions?vessel_id=${id}`,
});

export const CAPACITY_CRUD_CONFIG = createAppEntityCrudConfig<Capacity, Capacity>({
	apiSegment: "capacity",

	getByIdUrlTemplate: id => `capacity?vessel_id=${id}`,
});

export const DWT_CRUD_CONFIG = createAppEntityCrudConfig<Dwt, Dwt>({
	apiSegment: "dwt",

	getByIdUrlTemplate: id => `dwt?vessel_id=${id}`,
});

export const LOPM_CRUD_CONFIG = createAppEntityCrudConfig<Lopm, Lopm>({
	apiSegment: "lopm",

	getByIdUrlTemplate: id => `lopm?vessel_id=${id}`,
});

export const SLOP_DETAILS_CRUD_CONFIG = createAppEntityCrudConfig<SlopDetails, SlopDetails>({
	apiSegment: "bulk-carrier-specifications/slop-details",
});

export const TANKER_CARGO_HEATING_CRUD_CONFIG = createAppEntityCrudConfig<TankerCargoHeating, TankerCargoHeating>({
	apiSegment: "tanker-specifications/cargo-heating",

	getByIdUrlTemplate: id => `tanker-specifications/cargo-heating?vessel_id=${id}`,
});

export const TANKER_INERT_GAS_CRUD_CONFIG = createAppEntityCrudConfig<TankerInertGas, TankerInertGas>({
	apiSegment: "tanker-specifications/inert-gas",

	getByIdUrlTemplate: id => `tanker-specifications/inert-gas?vessel_id=${id}`,
});

export const TANKER_PV_VAPOR_CRUD_CONFIG = createAppEntityCrudConfig<TankerPvVapor, TankerPvVapor>({
	apiSegment: "tanker-specifications/pv-vapor",

	getByIdUrlTemplate: id => `tanker-specifications/pv-vapor?vessel_id=${id}`,
});

export const TANKER_MANIFOLD_CRUD_CONFIG = createAppEntityCrudConfig<TankerManifold, TankerManifold>({
	apiSegment: "tanker-specifications/manifold",

	getByIdUrlTemplate: id => `tanker-specifications/manifold?vessel_id=${id}`,
});

export const CONTAINER_MAIN_SPEC_CRUD_CONFIG = createAppEntityCrudConfig<ContainerMainSpec, ContainerMainSpec>({
	apiSegment: "container-specifications/spec",
	viewEntityName: "Main Specification",
});

export const OFFSHORE_BASIC_SPEC_CRUD_CONFIG = createAppEntityCrudConfig<OffshoreBasicSpec, OffshoreBasicSpec>({
	apiSegment: "offshore-specifications/basic-spec",
});

export const MAIN_MACHINERY_CRUD_CONFIG = createAppEntityCrudConfig<MainMachinery, MainMachinery>({
	apiSegment: "vessel-machinery/main-machinery",

	getByIdUrlTemplate: id => `vessel-machinery/main-machinery?vessel_id=${id}`,
});

export const DECK_MACHINERY_CRUD_CONFIG = createAppEntityCrudConfig<DeckMachinery, DeckMachinery>({
	apiSegment: "vessel-machinery/deck-machinery",

	getByIdUrlTemplate: id => `vessel-machinery/deck-machinery?vessel_id=${id}`,
});

// --- Vessel list entities ---

export const CONTAINER_HOLDS_CRUD_CONFIG = createAppEntityCrudConfig<ContainerHold, ContainerHold>({
	apiSegment: "bulk-carrier-specifications/container-holds",
	viewEntityName: "Container Holds",
});

export const HATCH_COVERS_CRUD_CONFIG = createAppEntityCrudConfig<HatchCover, HatchCover>({
	apiSegment: "bulk-carrier-specifications/hatch-covers",
	viewEntityName: "Hatch Cover",
});

export const CARGO_CRANES_CRUD_CONFIG = createAppEntityCrudConfig<CargoCrane, CargoCrane>({
	apiSegment: "bulk-carrier-specifications/cargo-cranes",
	viewEntityName: "Cargo Crane",
});

export const TANKER_CARGO_PUMPS_CRUD_CONFIG = createAppEntityCrudConfig<TankerCargoPump, TankerCargoPump>({
	apiSegment: "tanker-specifications/cargo-pumps",
	viewEntityName: "Cargo Pump",
});

export const TANKER_BALLAST_PUMP_CRUD_CONFIG = createAppEntityCrudConfig<TankerBallastPump, TankerBallastPump>({
	apiSegment: "tanker-specifications/ballast-pumps",
	viewEntityName: "Ballast Pump",
});

export const TANKER_CARGO_EDUCATORS_CRUD_CONFIG = createAppEntityCrudConfig<TankerCargoEducator, TankerCargoEducator>({
	apiSegment: "tanker-specifications/cargo-educators",
	viewEntityName: "Cargo Educator",
});

export const TANKER_BALLAST_EDUCATORS_CRUD_CONFIG = createAppEntityCrudConfig<TankerBallastEducator, TankerBallastEducator>({
	apiSegment: "tanker-specifications/ballast-educators",
	viewEntityName: "Ballast Educator",
});

export const TANKER_VAPOR_RETURNS_CRUD_CONFIG = createAppEntityCrudConfig<TankerVaporReturn, TankerVaporReturn>({
	apiSegment: "tanker-specifications/vapor-returns",
	viewEntityName: "Vapor Return",
});

export const TANKER_CRANE_DETAILS_CRUD_CONFIG = createAppEntityCrudConfig<TankerCraneDetail, TankerCraneDetail>({
	apiSegment: "tanker-specifications/cranes",
	viewEntityName: "Crane Detail",
});

export const CONTAINER_HOLDS_SPEC_CRUD_CONFIG = createAppEntityCrudConfig<ContainerHoldSpec, ContainerHoldSpec>({
	apiSegment: "container-specifications/holds",
	viewEntityName: "Hold",
});

export const CONTAINER_LASHING_CRUD_CONFIG = createAppEntityCrudConfig<ContainerLashing, ContainerLashing>({
	apiSegment: "container-specifications/lashing-equipment",
	viewEntityName: "Lashing Equipment",
});

export const CONTAINER_REEFER_SLOTS_CRUD_CONFIG = createAppEntityCrudConfig<ContainerReeferSlot, ContainerReeferSlot>({
	apiSegment: "container-specifications/reefer-slots",
	viewEntityName: "Reefer Slot",
});

export const CONTAINER_DECK_STRENGTHS_CRUD_CONFIG = createAppEntityCrudConfig<ContainerDeckStrength, ContainerDeckStrength>({
	apiSegment: "container-specifications/deck-strengths",
	viewEntityName: "Deck Strength",
});

export const CONTAINER_CRANE_DETAILS_CRUD_CONFIG = createAppEntityCrudConfig<ContainerCraneDetail, ContainerCraneDetail>({
	apiSegment: "container-specifications/cranes",
	viewEntityName: "Crane Detail",
});

export const OFFSHORE_WINCHES_CRUD_CONFIG = createAppEntityCrudConfig<OffshoreWinch, OffshoreWinch>({
	apiSegment: "offshore-specifications/winches",
	viewEntityName: "Winch",
});

export const OFFSHORE_CRANES_CRUD_CONFIG = createAppEntityCrudConfig<OffshoreCrane, OffshoreCrane>({
	apiSegment: "offshore-specifications/cranes",
	viewEntityName: "Crane",
});

export const OFFSHORE_FIREFIGHTING_CRUD_CONFIG = createAppEntityCrudConfig<OffshoreFirefighting, OffshoreFirefighting>({
	apiSegment: "offshore-specifications/firefighting",
	viewEntityName: "Firefighting System",
});

export const OFFSHORE_AZIMUTH_THRUSTERS_CRUD_CONFIG = createAppEntityCrudConfig<OffshoreAzimuthThruster, OffshoreAzimuthThruster>({
	apiSegment: "offshore-specifications/azimuth-thrusters",
	viewEntityName: "Azimuth Thruster",
});

export const OFFSHORE_TUNNEL_THRUSTERS_CRUD_CONFIG = createAppEntityCrudConfig<OffshoreTunnelThruster, OffshoreTunnelThruster>({
	apiSegment: "offshore-specifications/tunnel-thrusters",
	viewEntityName: "Tunnel Thruster",
});

export const DECK_EQUIPMENT_CRUD_CONFIG = createAppEntityCrudConfig<DeckEquipment, DeckEquipment>({
	apiSegment: "vessel-machinery/deck-equipment",
	viewEntityName: "Deck Equipment",
});

export const SPEED_CONDITIONS_CRUD_CONFIG = createAppEntityCrudConfig<SpeedCondition, SpeedCondition>({
	apiSegment: "vessel-machinery/speed-consumption/conditions",
	viewEntityName: "Condition",

	getByIdUrlTemplate: id => `vessel-machinery/speed-consumption/conditions?vessel_id=${id}`,
});

export const SPEED_CONSUMPTION_DATA_CRUD_CONFIG = createAppEntityCrudConfig<SpeedConsumptionData, SpeedConsumptionData>({
	apiSegment: "vessel-machinery/speed-consumption/data",
	viewEntityName: "Consumption Data",

	getByIdUrlTemplate: id => `vessel-machinery/speed-consumption/data?vessel_id=${id}`,
});

export const TANKS_CRUD_CONFIG = createAppEntityCrudConfig<Tank, Tank>({
	apiSegment: "vessel-tanks/tanks",
	viewEntityName: "Tank",
});

export const TANK_TYPES_CRUD_CONFIG = createAppEntityCrudConfig<TankType, TankType>({
	apiSegment: "vessel-tanks/tank-types",
	viewEntityName: "Tank Type",
});

export const BC_ITEMS_CRUD_CONFIG = createAppEntityCrudConfig<BcItem, BcItem>({
	apiSegment: "vessel-tanks/bc-items",
	viewEntityName: "B&C Item",
});

export const BC_TYPES_CRUD_CONFIG = createAppEntityCrudConfig<BcType, BcType>({
	apiSegment: "vessel-tanks/bc-types",
	viewEntityName: "B&C Type",
});

export const CONTRACTS_CRUD_CONFIG = createAppEntityCrudConfig<Contract, Contract>({
	apiSegment: "contracts",
	viewEntityName: "Contract",
});

export const INSURANCES_CRUD_CONFIG = createAppEntityCrudConfig<Insurance, Insurance>({
	apiSegment: "insurances",
	viewEntityName: "Insurance",
});

export const INSURANCE_INSTALLMENTS_CRUD_CONFIG = createAppEntityCrudConfig<InsuranceInstallment, InsuranceInstallment>({
	apiSegment: "insurances/installments",
	viewEntityName: "Installment",
});

export const CERTIFICATES_CRUD_CONFIG = createAppEntityCrudConfig<Certificate, Certificate>({
	apiSegment: "certificates",
	viewEntityName: "Certificate",
});

export const INDENTS_CRUD_CONFIG = createAppEntityCrudConfig<Indent, Indent>({
	apiSegment: "indents",
	viewEntityName: "Indent",
});

export const DEPARTMENTS_CRUD_CONFIG = createAppEntityCrudConfig<Department, Department>({
	apiSegment: "departments",
	viewEntityName: "Department",
});

export const INDENT_PRIORITIES_CRUD_CONFIG = createAppEntityCrudConfig<Priority, Priority>({
	apiSegment: "priorities",
	viewEntityName: "Priority",
	skipDefaultIdDescListSort: true,
});

export const SERVICE_GROUPS_CRUD_CONFIG = createAppEntityCrudConfig<ServiceGroup, ServiceGroup>({
	apiSegment: "service-groups",
	viewEntityName: "ServiceGroup",
});

export const BUDGET_CODES_CRUD_CONFIG = createAppEntityCrudConfig<BudgetCode, BudgetCode>({
	apiSegment: "budget-codes",
	viewEntityName: "BudgetCode",
});

export const BUDGET_ALLOCATIONS_CRUD_CONFIG = createAppEntityCrudConfig<BudgetAllocation, BudgetAllocation>({
	apiSegment: "budget-allocations",
	viewEntityName: "BudgetAllocation",
});

export const REASON_LABELS_CRUD_CONFIG = createAppEntityCrudConfig<ReasonLabel, ReasonLabel>({
	apiSegment: "reason-labels",
	viewEntityName: "ReasonLabel",
});

export const APPROVAL_MATRICES_CRUD_CONFIG = createAppEntityCrudConfig<ApprovalMatrix, ApprovalMatrix>({
	apiSegment: "approval-matrices",
	viewEntityName: "ApprovalMatrix",
});

export const RFQS_CRUD_CONFIG = createAppEntityCrudConfig<Rfq, Rfq>({
	apiSegment: "rfqs",
	viewEntityName: "Rfq",
});

export const VENDOR_QUOTATIONS_CRUD_CONFIG = createAppEntityCrudConfig<VendorQuotation, VendorQuotation>({
	apiSegment: "vendor-quotations",
	viewEntityName: "VendorQuotation",
});
